import { OfferDiscountType, type UsableOfferDto } from "../api/hooks/useOffers";

/** The parts of an offer that decide how its discount reads. The billing page holds these too. */
type DiscountTerms = Pick<
  UsableOfferDto,
  "discountType" | "discountValue" | "durationMonths"
>;

/**
 * How an offer describes itself. Separate from the card because the cart says the same things about
 * the same offer, and two copies would drift the moment one of them is reworded.
 */

/** "Free month" reads better than "100% off", and it is the same offer. */
export function discountLabel(
  offer: DiscountTerms,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (offer.discountType === OfferDiscountType.Percent) {
    return offer.discountValue >= 100 && offer.durationMonths === 1
      ? t("discount.freeMonth")
      : t("discount.percentOff", { value: Number(offer.discountValue.toFixed(2)) });
  }

  return t("discount.amountOff", { value: offer.discountValue.toFixed(2) });
}

/** What it can be spent on, and how many times — the questions the wording leaves open. */
export function termsLine(
  offer: UsableOfferDto,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const scope =
    offer.tradingStrategyIds.length === 0
      ? t("terms.anyStrategy")
      : t("terms.selectedStrategies", { count: offer.tradingStrategyIds.length });

  const cap =
    offer.maxStrategies === null
      ? t("terms.noLimit")
      : t("terms.strategyLimit", { count: offer.maxStrategies });

  return t("terms.combined", { scope, cap });
}
